/**
 * The register's own reference rules — the ones only «Rozet ve Sertifikalar» and its three
 * dialogs use.
 *
 * `reference.ts` carries what every screen needs and is shared, so the list filter
 * (`filteredCreds`, `m08b-admin2.js:15`), the state machine the revoke command checks
 * (`STATE_TRANSITIONS`, `m03-state.js:7`), the claim-URL status a resend dialog reports
 * (`claimUrlStatus`, `m03-state.js:52`) and the demo-side effects of the three domain commands
 * the screen calls are ported here instead.
 *
 * The commands mutate a draft database — the provider hands `update()` a structural clone — and
 * every one of them is the prototype's simulation, not a backend call. What a real backend would
 * have to do instead is named at each site.
 */
import type {
  CredentialState,
  DemoCredential,
  DemoDatabase,
  DemoEvent,
  DemoFieldDefinition,
  DemoRelease,
} from '../data/types';
import { validateFieldValue } from './publish-gate';
import {
  credentialViewState,
  displayName,
  editableFields,
  fieldLabel,
  nowUtcSeconds,
  stateForPolicy,
  trLower,
  trUpper,
} from './reference';

/** `shortCode` — `m01-utils.js:214`. The first three groups; a table cell never shows the rest. */
export const shortCode = (code: string | null | undefined): string =>
  `${String(code ?? '')
    .split('-')
    .slice(0, 3)
    .join('-')}-…`;

/**
 * `normalizeCode` — `m01-utils.js:213`.
 *
 * The confusable folding (I/L→1, O→0) exists so a code someone typed off a printed certificate
 * still finds its record. It widens the SEARCH only; uniqueness is never relaxed by it.
 */
export const normalizeCode = (s: string | null | undefined): string =>
  trUpper(String(s ?? ''))
    .replace(/[^A-Z0-9]/g, '')
    .replace(/^HPW/, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');

// ------------------------------------------------------------------ the list filter (`m08b:15`)

export interface CredFilters {
  q: string;
  status: string;
  template: string;
  /** Not a control: the dashboard's «Gör» links carry it, and «filtreyi kaldır» drops it. */
  delivery: string | null;
  batch: string | null;
  /** One group's members (migration 028) — where the operator put a record, not where it came from. */
  group?: string | null;
  /**
   * «Hiç e-posta gönderilmemiş» — and it is NOT `delivery=none`.
   *
   * `delivery` reads the record's latest claim attempt, which an immediately published certificate
   * never has whether or not it was notified. This asks whether a delivery EXISTS, which is the
   * question the first send turns on, so this surface answers it the same way rather than leaving
   * the parameter to be ignored where a filter was asked for.
   */
  noEmail?: boolean;
}

/**
 * «Has an e-mail been sent for this record?», as this surface can answer it.
 *
 * One function, because the list filter and the first-send preview must not be able to disagree:
 * a register that offers a record and a window that then says «it already has an e-mail» is worse
 * than either answer on its own. `disabled` and `none` are this projection's words for «there was
 * no claim delivery», not for a delivery with those names — the server's enum has neither — so they
 * are not evidence of an e-mail; a row in `deliveries` is.
 */
export function hasAnyEmail(db: DemoDatabase, c: DemoCredential): boolean {
  if (db.deliveries.some((d) => d.credentialId === c.id)) return true;
  const s = c.deliveryStatus ?? '';
  return s !== '' && s !== 'disabled' && s !== 'none';
}

export function filterCredentials(db: DemoDatabase, f: CredFilters): DemoCredential[] {
  const s = trLower(f.q.trim());
  return db.credentials
    .filter((c) => {
      if (f.status && credentialViewState(c) !== f.status) return false;
      if (f.template && c.templateId !== f.template) return false;
      if (f.delivery) {
        /* `pending` covers both names the seed uses for «not delivered yet». */
        const hit =
          f.delivery === 'pending'
            ? c.deliveryStatus === 'queued' || c.deliveryStatus === 'pending'
            : c.deliveryStatus === f.delivery;
        if (!hit) return false;
      }
      if (f.batch && c.batchId !== f.batch) return false;
      /*
       * Group membership (migration 028). Read from the group's own list, never from `batchId`:
       * the upload a record came from is provenance and is not where an operator put it.
       */
      if (f.group) {
        const g = (db.credentialGroups ?? []).find((x) => x.id === f.group);
        if (!g?.credentialIds.includes(c.id)) return false;
      }
      if (f.noEmail && hasAnyEmail(db, c)) return false;
      if (!s) return true;
      return [displayName(c), c.privateContactEmail, c.publicCode, normalizeCode(c.publicCode)].some(
        (x) => x != null && x !== '' && trLower(String(x)).includes(s),
      );
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

// ------------------------------------------------------- state machine and action context (`m03`)

export const STATE_TRANSITIONS: Record<string, string[]> = {
  draft: ['pending_acceptance', 'pending_admin_publish', 'active', 'revoked'],
  pending_acceptance: ['active', 'revoked'],
  pending_admin_publish: ['active', 'revoked'],
  active: ['revoked'],
  revoked: [],
};

export const canTransition = (from: string, to: string): boolean =>
  (STATE_TRANSITIONS[from] ?? []).includes(to);

export const REASON_MIN_LENGTH = 3;

export const ACTION_REASON_TR: Record<string, string> = {
  reason_required: `Gerekçe zorunludur ve en az ${REASON_MIN_LENGTH} karakter olmalıdır.`,
  actor_required: 'İşlemi yapan yönetici (actor) zorunludur.',
};

export const hasActionReason = (reason: string): boolean => reason.trim().length >= REASON_MIN_LENGTH;

// ------------------------------------------------------------------- claim token and outbox view

interface OutboxEntry {
  rawToken?: string;
  claimUrl?: string;
}

function outbox(db: DemoDatabase): Record<string, OutboxEntry> {
  const box = (db as { outbox?: unknown }).outbox;
  return box !== null && typeof box === 'object' ? (box as Record<string, OutboxEntry>) : {};
}

export const rawClaimToken = (db: DemoDatabase, c: DemoCredential): string | null =>
  outbox(db)[c.id]?.rawToken ?? null;

export interface ClaimTokenView {
  hash: string | null;
  expiresAt: string | null;
  consumedAt: string | null;
  revokedAt: string | null;
  rotationCount: number;
}

/** The seed types `claimToken` loosely; the four fields the dialogs read are narrowed here. */
export function claimTokenView(c: DemoCredential): ClaimTokenView | null {
  const t = c.claimToken;
  if (t === null || t === undefined) return null;
  const str = (k: string): string | null => (typeof t[k] === 'string' ? (t[k] as string) : null);
  return {
    hash: str('hash'),
    expiresAt: str('expiresAt'),
    consumedAt: str('consumedAt'),
    revokedAt: str('revokedAt'),
    rotationCount: typeof t.rotationCount === 'number' ? t.rotationCount : 0,
  };
}

export interface ClaimUrlStatus {
  available: boolean;
  url: string | null;
  reason: string | null;
  message: string;
}

/**
 * `claimUrlStatus` — `m03-state.js:52`, and the reason it is a structured answer rather than a
 * string: v0.6 returned `…/claim/…` when the raw token was gone, which copied and pasted as if it
 * were a working link. Nothing here may produce a link the recipient cannot use.
 */
export function claimUrlStatus(db: DemoDatabase, c: DemoCredential): ClaimUrlStatus {
  const entry = outbox(db)[c.id];
  if (entry?.rawToken && entry.claimUrl)
    return { available: true, url: entry.claimUrl, reason: null, message: '' };
  const tok = claimTokenView(c);
  if (tok === null)
    return {
      available: false,
      url: null,
      reason: 'no_claim_token',
      message: 'Bu kayıt onay bağlantısı taşımıyor.',
    };
  if (tok.consumedAt)
    return {
      available: false,
      url: null,
      reason: 'token_consumed',
      message: 'Onay bağlantısı kullanıldı.',
    };
  if (tok.revokedAt)
    return {
      available: false,
      url: null,
      reason: 'token_revoked',
      message: 'Onay bağlantısı iptal edildi.',
    };
  /*
   * A lapsed link and a link this session simply does not hold are different facts with different
   * next steps, and saying «üretin» about a link that is still valid in the recipient's inbox
   * invites a send nobody needed. The clock is the screen's own view clock, so the demo answers
   * from the frozen date and the product from the real instant.
   */
  if (tok.expiresAt && String(tok.expiresAt) < nowUtcSeconds())
    return {
      available: false,
      url: null,
      reason: 'token_expired',
      message: 'Onay bağlantısının süresi doldu.',
    };
  return {
    available: false,
    url: null,
    reason: 'raw_token_not_in_session',
    message:
      'Kullanılamıyor — yeni bağlantı üretin. Ham token yalnız üretildiği oturumda bellekte tutulur; sayfa yenilendikten sonra geri getirilemez.',
  };
}

// ------------------------------------------------------------------------------ demo-side minting

const CROCK32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function crockford(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += CROCK32[b % 32];
  return out;
}

function hex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
}

let seq = 0;
const uid = (prefix: string): string => `${prefix}_${(++seq).toString(36)}${hex(3)}`;

export const newCorrelationId = (): string => `corr_${hex(6)}`;

/** `formatToken('CLM', …)` — `m01-utils.js:147`: groups of six, 52 Crockford characters. */
export const newClaimTokenRaw = (): string => `CLM-${(crockford(52).match(/.{1,6}/g) ?? []).join('-')}`;

/**
 * `prototypeNowUTC` — the reference's clock, now served by the view clock.
 *
 * In the demo it still reads the frozen date with the real time of day, exactly as before. In the
 * product it reads the real instant, which is what the claim gate's expiry comparison and every
 * locally stamped record needed: a token that lapsed yesterday cannot be judged against a date in
 * the source. `reference.ts` holds the single implementation — four copies of a clock is four
 * chances for one of them to stay frozen.
 */
export const prototypeNow = nowUtcSeconds;

function addDaysUTC(iso: string, days: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setUTCDate(d.getUTCDate() + days);
  return `${d.toISOString().slice(0, 19)}Z`;
}

function claimValidityDays(db: DemoDatabase): number {
  const v = (db.settings.claim as { tokenValidityDays?: unknown }).tokenValidityDays;
  return typeof v === 'number' ? v : 30;
}

// -------------------------------------------------------------------- append-only audit (`m02:225`)

interface EventInput {
  type: string;
  command?: string;
  at: string;
  actor?: string;
  actorType?: string;
  reason?: string | null;
  meta?: Record<string, unknown>;
  beforeDigest?: string | null;
  afterDigest?: string | null;
  correlationId?: string | null;
}

function appendEvent(db: DemoDatabase, c: DemoCredential | null, o: EventInput): DemoEvent {
  const ev: DemoEvent = {
    id: uid('ev'),
    credentialId: c ? c.id : null,
    type: o.type,
    command: o.command ?? o.type,
    at: o.at,
    actor: o.actor ?? 'Sistem',
    actorType: o.actorType ?? 'system',
    reason: o.reason ?? null,
    meta: o.meta ?? {},
    beforeDigest: o.beforeDigest ?? null,
    afterDigest: o.afterDigest ?? null,
  };
  if (o.correlationId) ev.correlationId = o.correlationId;
  if (c) {
    c.events.push(ev);
    c.updatedAt = ev.at;
  }
  db.eventLog.push(ev);
  return ev;
}

// --------------------------------------------------------------------------- the three commands

/**
 * `revokeCredential` — `m03-state.js:260`.
 *
 * API GAP: credential.revoke — a single transaction that writes the state, revokes any unconsumed
 * claim token and appends the audit event; the demo does all three on the in-memory clone.
 */
export function revokeInDraft(db: DemoDatabase, code: string, reason: string, actor: string): void {
  const c = db.credentials.find((x) => x.publicCode === code);
  if (!c || !canTransition(c.state, 'revoked')) return;
  const at = prototypeNow();
  const before = c.state;
  c.state = 'revoked';
  c.revokedAt = at;
  c.revokedReasonPrivate = reason.trim();
  const tok = claimTokenView(c);
  if (c.claimToken && tok && !tok.consumedAt) c.claimToken = { ...c.claimToken, revokedAt: at };
  appendEvent(db, c, {
    type: 'revoked',
    command: 'revokeCredential',
    actor,
    actorType: 'admin',
    at,
    reason: reason.trim(),
    beforeDigest: before,
    afterDigest: c.state,
    correlationId: newCorrelationId(),
  });
}

/**
 * `resendCredentialEmail` — `m03-state.js:415`.
 *
 * The rule this reproduces: a resend on a record still awaiting acceptance ROTATES the token, so
 * the link already in the recipient's inbox stops working. A published record only gets the
 * notification again.
 *
 * API GAP: credential.resend — mints and hashes the token server-side, supersedes the open
 * delivery attempts and queues a real Resend send. The demo mints a look-alike token locally; the
 * raw value never leaves the tab and only its hash is stored on the record.
 */
export function resendInDraft(db: DemoDatabase, code: string, actor: string, reason: string): void {
  const c = db.credentials.find((x) => x.publicCode === code);
  if (!c || c.state === 'revoked') return;
  const pending = c.state === 'pending_acceptance';
  const at = prototypeNow();
  const correlationId = newCorrelationId();

  if (pending) {
    const previous = claimTokenView(c);
    if (c.claimToken && previous && !previous.consumedAt) c.claimToken = { ...c.claimToken, revokedAt: at };
    const raw = newClaimTokenRaw();
    c.claimToken = {
      hash: hex(32),
      algorithm: 'sha256',
      entropyBits: 256,
      encoding: 'base32',
      charCount: 52,
      bitsPerChar: 5,
      issuedAt: at,
      expiresAt: addDaysUTC(at, claimValidityDays(db)),
      consumedAt: null,
      revokedAt: null,
      rotationCount: (previous?.rotationCount ?? -1) + 1,
      previousHash: previous?.hash ?? null,
    };
    const box = outbox(db);
    box[c.id] = {
      rawToken: raw,
      claimUrl: `${c.release.issuerProfileSnapshot.publicBase}/claim/${raw}`,
    };
    appendEvent(db, c, {
      type: 'claim_token_rotated',
      command: 'resendCredentialEmail',
      actor,
      actorType: 'admin',
      at,
      reason: reason.trim(),
      correlationId,
      meta: {
        revokedHash: previous?.hash ? previous.hash.slice(0, 12) : null,
        rotationCount: claimTokenView(c)?.rotationCount ?? 0,
      },
    });
    for (const d of db.deliveries)
      if (d.credentialId === c.id && d.state !== 'superseded') d.state = 'superseded';
  }

  const tok = claimTokenView(c);
  const delivery = {
    id: uid('dlv'),
    credentialId: c.id,
    tokenHash: tok?.hash ?? null,
    tokenRotation: tok?.rotationCount ?? null,
    recipientEmail: c.privateContactEmail ?? '',
    state: 'sent',
    createdAt: at,
    queuedAt: at,
    sentAt: at,
    deliveredAt: null,
    bouncedAt: null,
    failedAt: null,
    failureCode: null,
    correlationId,
  };
  appendEvent(db, c, {
    type: 'email_queued',
    command: 'resendCredentialEmail',
    at,
    correlationId,
    meta: { to: c.privateContactEmail, deliveryId: delivery.id, rotated: pending },
  });
  appendEvent(db, c, {
    type: 'email_sent',
    command: 'resendCredentialEmail',
    actor: 'Resend (simülasyon)',
    at,
    correlationId,
    meta: { deliveryId: delivery.id },
  });
  db.deliveries.push(delivery);
  c.deliveryStatus = 'sent';
  c.updatedAt = at;
}

// -------------------------------------------------------------------------------- single issuance

export interface IssueProblem {
  key: string | null;
  message: string;
}

/**
 * Which field carries the name a credential is made out to — resolved against the fields the
 * release ACTUALLY has.
 *
 * The rule used to be `release.holderField` with a conventional fallback, and that string is part
 * of the v0.7 layout model rather than of the field set: when the two disagree, the key names no
 * field, nothing collects a value for it, and `subject.displayName` goes out empty. The backend
 * then refuses the whole request — `subject.displayName` must have at least one character — and a
 * form that looked complete came back «request shape is invalid».
 *
 * So the layout's own answer is still preferred, and it is only BELIEVED when it names one of the
 * release's fields. After that comes the field the release itself marks as the holder (the
 * backend's `holderRole`, which its field schema carries), then the conventional key if a field
 * has it. Nothing is invented: every answer is a field this release declares. When none of the
 * three resolves, the caller is told there is no holder field rather than handed a guess.
 */
const HOLDER_ROLES: Record<'person' | 'organization', string[]> = {
  person: ['subject_person', 'recipient_name'],
  organization: ['subject_org', 'organization_name'],
};

export function holderFieldOfRelease(release: DemoRelease): DemoFieldDefinition | null {
  const defs: DemoFieldDefinition[] = release.fieldDefinitions ?? [];
  const roles = HOLDER_ROLES[release.subjectType === 'organization' ? 'organization' : 'person'];
  const named = release.holderField ? defs.find((f) => f.key === release.holderField) : undefined;
  if (named) return named;
  const byRole = defs.find((f) => f.role !== null && roles.includes(String(f.role)));
  if (byRole) return byRole;
  return defs.find((f) => roles.includes(f.key)) ?? null;
}

/** The holder key, for the callers that need a key even when the release declares no such field. */
export function holderKeyOfRelease(release: DemoRelease): string {
  return (
    holderFieldOfRelease(release)?.key ??
    release.holderField ??
    (release.subjectType === 'organization' ? 'organization_name' : 'recipient_name')
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Fail-closed (rule 11): one missing required value stops the issuance and nothing is created. */
/**
 * One record's inputs, against the release's own field schema.
 *
 * It used to check two things — requiredness and the e-mail address — and nothing about the TYPE
 * the administrator had chosen. So a required «Evet / Hayır» field accepted any sentence, and the
 * screen's own help line («tek kayıt, CSV ve XLSX aynı doğrulama motorunu kullanır») was not true.
 * It is now: this is the same `validateFieldValue` the batch import and the publish gate run.
 */
export function validateIssue(
  release: DemoRelease,
  claims: Record<string, string>,
  email: string,
  lang: 'tr' | 'en',
): IssueProblem[] {
  const problems: IssueProblem[] = [];
  for (const f of editableFields<DemoFieldDefinition>(release.fieldDefinitions)) {
    if (f.required && (claims[f.key] ?? '').trim() === '') {
      problems.push({ key: f.key, message: `${fieldLabel(f, lang)} zorunludur.` });
      continue;
    }
    if ((claims[f.key] ?? '').trim() === '') continue;
    const r = validateFieldValue(f as never, claims[f.key]);
    for (const e of r.errors) problems.push({ key: f.key, message: `${fieldLabel(f, lang)}: ${e.message}` });
  }
  /*
   * And the holder, which is not one of the fields above unless the template says so.
   *
   * `subject.displayName` is built from this field and the backend refuses an empty one, so a
   * template whose holder field the form never asks for produces a request that CANNOT succeed —
   * and used to produce it silently, with every visible input filled in. Checked here, where the
   * screen can say which field it is, rather than at the server, where it is a shape error.
   */
  const holder = holderFieldOfRelease(release);
  const collected =
    holder !== null &&
    editableFields<DemoFieldDefinition>(release.fieldDefinitions).some((f) => f.key === holder.key);
  if (!collected)
    problems.push({
      key: 'holder',
      message: `Bu şablonun sahip alanı («${holderKeyOfRelease(release)}») kayıt formunda yok; kaydın kime düzenleneceği okunamıyor. Şablonun alanlarını düzeltip yeniden yayınlayın.`,
    });
  else if ((claims[holder.key] ?? '').trim() === '')
    problems.push({
      key: holder.key,
      message: `${fieldLabel(holder, lang)} zorunludur; kaydın kime düzenlendiği bu alandan okunur.`,
    });

  const mail = email.trim();
  if (mail === '') problems.push({ key: 'email', message: 'İletişim e-postası zorunludur.' });
  else if (!EMAIL_RE.test(mail))
    problems.push({ key: 'email', message: 'İletişim e-postası geçerli bir adres değil.' });
  return problems;
}

/**
 * The same values, in the form the schema stores them.
 *
 * `validateFieldValue` normalises as it validates — a «Evet» becomes `'true'`, a padded number its
 * digits — and the record must carry what was validated, not what was typed. Anything that does not
 * validate is left exactly as it was, because `validateIssue` has already refused the form.
 */
export function normalizeIssueClaims(
  release: DemoRelease,
  claims: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = { ...claims };
  for (const f of editableFields<DemoFieldDefinition>(release.fieldDefinitions)) {
    const raw = claims[f.key];
    if (raw === undefined || raw.trim() === '') continue;
    const r = validateFieldValue(f as never, raw);
    if (r.ok) out[f.key] = String(r.value);
  }
  return out;
}

export interface IssueInput {
  release: DemoRelease;
  claims: Record<string, string>;
  email: string;
  issueDate: string;
  expiresAt: string | null;
  lang: 'tr' | 'en';
  sendEmail: boolean;
  actor: string;
  /** Minted before the mutation so the caller can navigate to the new record. */
  publicCode: string;
}

/** The reference's own uniqueness rule for a minted code (rule 6): HPW- plus five groups of four. */
export function mintPublicCode(db: DemoDatabase): string {
  for (let attempt = 0; attempt < 20; attempt++) {
    const code = `HPW-${(crockford(20).match(/.{1,4}/g) ?? []).join('-')}`;
    if (!db.credentials.some((c) => c.publicCode === code)) return code;
  }
  throw new Error('Public kod üretilemedi');
}

/**
 * `issueCredentialFromRelease` — `m03b-issuance.js`, reduced to what the demo can honestly do.
 *
 * API GAP: credential.issue — the real command validates against the release's field definitions
 * server-side, mints the public code and the claim token there, renders the artifact and records
 * the render manifest hash. The demo writes the record and leaves the manifest empty, because a
 * hash it invented would be a claim about bytes that were never produced (rule 10).
 */
export function issueInDraft(db: DemoDatabase, input: IssueInput): void {
  const rel = input.release;
  const at = prototypeNow();
  const correlationId = newCorrelationId();
  const holderKey = holderKeyOfRelease(rel);
  const name = (input.claims[holderKey] ?? '').trim();

  /* Rule 5: identity is never merged on a name, so issuance always opens a new subject record. */
  const subjectId = uid('subj');
  db.subjects.push({
    id: subjectId,
    subjectType: rel.subjectType,
    canonicalDisplayName: name,
    externalReference: null,
    externalKey: null,
    privateContactEmail: input.email.trim(),
    createdAt: at,
  });

  /* `stateForPolicy` speaks the view vocabulary, which is wider than what a record may be stored
     as — `draft` and `expired` are derived, never written. */
  const opens = stateForPolicy(rel.publicationPolicy);
  const state: CredentialState =
    opens === 'active'
      ? 'active'
      : opens === 'pending_admin_publish'
        ? 'pending_admin_publish'
        : 'pending_acceptance';
  const base = rel.issuerProfileSnapshot.publicBase;
  const c: DemoCredential = {
    id: uid('cred'),
    publicCode: input.publicCode,
    canonicalPublicUrl: `${base}/c/${input.publicCode}`,
    templateId: rel.templateId,
    releaseId: rel.id,
    releaseHash: rel.releaseHash,
    release: rel,
    subjectId,
    subjectSnapshot: {
      subjectId,
      subjectType: rel.subjectType,
      canonicalDisplayName: name,
      externalReference: null,
      capturedAt: at,
    },
    claims: { ...input.claims },
    privateContactEmail: input.email.trim(),
    lang: input.lang,
    issueDate: input.issueDate,
    expiresAt: input.expiresAt,
    state,
    publishedAt: state === 'active' ? at : null,
    revokedAt: null,
    supersedesCode: null,
    supersededByCode: null,
    renderManifest: {},
    renderManifestHash: '',
    revision: 1,
    revisions: [],
    claimToken: null,
    deliveryStatus: input.sendEmail ? 'sent' : 'disabled',
    batchId: null,
    sourceRow: null,
    createdBy: input.actor,
    createdAt: at,
    updatedAt: at,
    events: [],
  };

  if (state === 'pending_acceptance') {
    const raw = newClaimTokenRaw();
    c.claimToken = {
      hash: hex(32),
      algorithm: 'sha256',
      entropyBits: 256,
      encoding: 'base32',
      charCount: 52,
      bitsPerChar: 5,
      issuedAt: at,
      expiresAt: addDaysUTC(at, claimValidityDays(db)),
      consumedAt: null,
      revokedAt: null,
      rotationCount: 0,
      previousHash: null,
    };
    outbox(db)[c.id] = { rawToken: raw, claimUrl: `${base}/claim/${raw}` };
  }

  db.credentials.push(c);
  appendEvent(db, c, {
    type: 'created',
    command: 'issueCredentialFromRelease',
    actor: input.actor,
    actorType: 'admin',
    at,
    correlationId,
    meta: { releaseId: rel.id, publicCode: c.publicCode },
  });
  if (input.sendEmail) {
    const delivery = {
      id: uid('dlv'),
      credentialId: c.id,
      tokenHash: claimTokenView(c)?.hash ?? null,
      tokenRotation: claimTokenView(c)?.rotationCount ?? null,
      recipientEmail: c.privateContactEmail ?? '',
      state: 'sent',
      createdAt: at,
      queuedAt: at,
      sentAt: at,
      deliveredAt: null,
      bouncedAt: null,
      failedAt: null,
      failureCode: null,
      correlationId,
    };
    db.deliveries.push(delivery);
    appendEvent(db, c, {
      type: 'email_sent',
      command: 'issueCredentialFromRelease',
      actor: 'Resend (simülasyon)',
      at,
      correlationId,
      meta: { deliveryId: delivery.id },
    });
  }
}
